import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { jsPDF } from 'jspdf';
import styles from '../assets/propertyInspections.module.css';
import inspectionsService from '../services/inspectionsService';
import propertyInspectionsService from '../services/propertyInspectionsService';

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const PropertyInspections = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [property, setProperty] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [inspectionsAll, setInspectionsAll] = useState(0);
  const [inspectionsUpcoming, setInspectionsUpcoming] = useState(0);
  const [inspectionsPast, setInspectionsPast] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(999); // All
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false); // Try backend API first
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [buildingManagers, setBuildingManagers] = useState([]);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [formData, setFormData] = useState({
    property_id: '',
    building_manager_id: '',
    type_id: '',
    day: '',
    time_from: '',
    time_to: '',
    description: '',
    updated_at: ''
  });
  const [propertySearchValue, setPropertySearchValue] = useState('');
  const [propertySearchResults, setPropertySearchResults] = useState([]);
  const [showPropertyTypeahead, setShowPropertyTypeahead] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Mock data generation
  const generateMockProperty = () => ({
    id: parseInt(id) || 1,
    name: 'Sample Property',
    address: '123 Sample Street, London',
    postcode: 'SW1A 1AA'
  });

  const generateMockInspections = () => [
    {
      id: 1,
      buildingManagerName: 'John Smith',
      day: '2024-01-15',
      time_from: '09:00',
      time_to: '10:00',
      typeName: 'Monthly Inspection',
      status: 'upcoming',
      calendar: 'View',
      actions: 'Edit'
    },
    {
      id: 2,
      buildingManagerName: 'Jane Doe',
      day: '2024-01-20',
      time_from: '14:00',
      time_to: '15:00',
      typeName: 'Initial Inspection',
      status: 'upcoming',
      calendar: 'View',
      actions: 'Edit'
    },
    {
      id: 3,
      buildingManagerName: 'Mike Johnson',
      day: '2024-01-10',
      time_from: '11:00',
      time_to: '12:00',
      typeName: 'Follow-up Inspection',
      status: 'past',
      calendar: 'View',
      actions: 'Edit'
    }
  ];

  const generateMockBuildingManagers = () => [
    { id: 1, name: 'John Smith' },
    { id: 2, name: 'Jane Doe' },
    { id: 3, name: 'Mike Johnson' }
  ];

  const generateMockInspectionTypes = () => [
    { id: 1, name: 'Monthly Inspection' },
    { id: 2, name: 'Initial Inspection' },
    { id: 3, name: 'Follow-up Inspection' },
    { id: 4, name: 'Emergency Inspection' }
  ];

  // Load data
  useEffect(() => {
    loadData();
  }, [id, useMockData]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (useMockData) {
        const mockProperty = generateMockProperty();
        const mockInspections = generateMockInspections();
        const mockBuildingManagers = generateMockBuildingManagers();
        const mockInspectionTypes = generateMockInspectionTypes();
        
        setProperty(mockProperty);
        setInspections(mockInspections);
        setBuildingManagers(mockBuildingManagers);
        setInspectionTypes(mockInspectionTypes);
        
        // Calculate counts
        setInspectionsAll(mockInspections.length);
        setInspectionsUpcoming(mockInspections.filter(i => i.status === 'upcoming').length);
        setInspectionsPast(mockInspections.filter(i => i.status === 'past').length);
      } else {
        // Real API calls
        const propertyResponse = await propertyInspectionsService.getPropertyDetails(id);
        setProperty(propertyResponse.data);
        
        const inspectionsResponse = await propertyInspectionsService.getInspections(id);
        // Backend returns { success: true, data: [...] }, axios wraps it in .data
        // So inspectionsResponse.data = { success: true, data: [...] }
        setInspections(inspectionsResponse.data?.data || inspectionsResponse.data || []);
        
        const buildingManagersResponse = await propertyInspectionsService.getBuildingManagers();
        setBuildingManagers(buildingManagersResponse.data?.data || buildingManagersResponse.data || []);
        
        const typesResponse = await propertyInspectionsService.getInspectionTypes();
        setInspectionTypes(typesResponse.data?.data || typesResponse.data || []);
        
        // Get counts from API
        const countsResponse = await propertyInspectionsService.getInspectionCounts(id);
        const countsData = countsResponse.data?.data || countsResponse.data || { all: 0, upcoming: 0, past: 0 };
        setInspectionsAll(countsData.all || 0);
        setInspectionsUpcoming(countsData.upcoming || 0);
        setInspectionsPast(countsData.past || 0);
      }
    } catch (error) {
      console.error('Error loading property inspections data:', error);
      toast.error('Failed to load property inspections.');
    } finally {
      setLoading(false);
    }
  };

  // Convert inspections to calendar events
  const calendarEvents = inspections.map(inspection => ({
    id: inspection.id,
    title: `${inspection.typeName} - ${inspection.buildingManagerName}`,
    start: inspection.day && inspection.time_from ? new Date(`${inspection.day} ${inspection.time_from}`) : new Date(),
    end: inspection.day && inspection.time_to ? new Date(`${inspection.day} ${inspection.time_to}`) : new Date(),
    resource: inspection
  }));

  // Handle status filter
  const handleStatusFilter = (statusId) => {
    setSelectedStatus(statusId);
    // In real implementation, this would reload the data with the filter
    toast.info(`Filtering by status: ${statusId}`);
  };

  // Handle calendar event click
  const handleEventClick = (event) => {
    setEditingInspection(event.resource);
    const propertyName = event.resource.property?.name || property?.name || '';
    setPropertySearchValue(propertyName);
    setFormData({
      property_id: property?.id || '',
      property_title: propertyName,
      building_manager_id: event.resource.buildingManagerId || event.resource.building_manager_id || '',
      type_id: event.resource.typeId || event.resource.type_id || '',
      day: event.resource.day || '',
      time_from: event.resource.time_from || '',
      time_to: event.resource.time_to || '',
      description: event.resource.description || '',
      updated_at: event.resource.updated_at || ''
    });
    setShowEditModal(true);
  };

  // Handle calendar event drag (reschedule)
  const handleEventDrop = async (event) => {
    const day = format(event.start, 'dd/MM/yyyy');
    const timeFrom = format(event.start, 'HH:mm');
    const timeTo = format(event.end, 'HH:mm');
    const start = `${day} ${timeFrom}`;
    const end = `${day} ${timeTo}`;

    try {
      await inspectionsService.rescheduleInspection(event.resource.id, start, end);
      toast.success('Inspection rescheduled successfully!');
      loadData();
    } catch (error) {
      console.error('Error rescheduling inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to reschedule inspection');
      loadData(); // Reload to revert the change
    }
  };

  // Handle calendar event resize (reschedule)
  const handleEventResize = async (event) => {
    const day = format(event.start, 'dd/MM/yyyy');
    const timeFrom = format(event.start, 'HH:mm');
    const timeTo = format(event.end, 'HH:mm');
    const start = `${day} ${timeFrom}`;
    const end = `${day} ${timeTo}`;

    try {
      await inspectionsService.rescheduleInspection(event.resource.id, start, end);
      toast.success('Inspection rescheduled successfully!');
      loadData();
    } catch (error) {
      console.error('Error rescheduling inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to reschedule inspection');
      loadData(); // Reload to revert the change
    }
  };

  // Handle property search
  const handlePropertySearch = async (value) => {
    setPropertySearchValue(value);
    if (value.length >= 2) {
      try {
        const response = await inspectionsService.searchProperties(value);
        setPropertySearchResults(response.data || []);
        setShowPropertyTypeahead(true);
      } catch (error) {
        console.error('Error searching properties:', error);
        setPropertySearchResults([]);
      }
    } else {
      setPropertySearchResults([]);
      setShowPropertyTypeahead(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (selectedProperty) => {
    setFormData({
      ...formData,
      property_id: selectedProperty.id
    });
    setPropertySearchValue(selectedProperty.name);
    setShowPropertyTypeahead(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle add inspection
  const handleAddInspection = () => {
    setFormData({
      property_id: property?.id || '',
      building_manager_id: '',
      type_id: '',
      day: '',
      time_from: '',
      time_to: '',
      description: '',
      updated_at: ''
    });
    setPropertySearchValue(property?.name || '');
    setShowAddModal(true);
  };

  // Handle edit inspection
  const handleEditInspection = (inspection) => {
    setEditingInspection(inspection);
    setFormData({
      property_id: property?.id || '',
      building_manager_id: inspection.buildingManagerId || inspection.building_manager_id || '',
      type_id: inspection.typeId || inspection.type_id || '',
      day: inspection.day || '',
      time_from: inspection.time_from || '',
      time_to: inspection.time_to || '',
      description: inspection.description || '',
      updated_at: inspection.updated_at || ''
    });
    setPropertySearchValue(property?.name || '');
    setShowEditModal(true);
  };

  // Handle delete inspection
  const handleDeleteInspection = async (inspectionId) => {
    if (!window.confirm('Are you sure you want to delete this inspection?')) {
      return;
    }
    
    try {
      await inspectionsService.deleteInspection(inspectionId);
      toast.success('Inspection deleted successfully.');
      setShowEditModal(false);
      setEditingInspection(null);
      loadData();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error('Failed to delete inspection');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    setFormLoading(true);
    try {
      if (showAddModal) {
        await inspectionsService.createInspection({
          property_id: formData.property_id,
          building_manager_id: formData.building_manager_id,
          type_id: formData.type_id,
          day: formData.day,
          time_from: formData.time_from,
          time_to: formData.time_to,
          description: formData.description
        });
        toast.success('Inspection created successfully.');
        setShowAddModal(false);
      } else if (showEditModal && editingInspection) {
        await inspectionsService.updateInspection(editingInspection.id, {
          property_id: formData.property_id,
          building_manager_id: formData.building_manager_id,
          type_id: formData.type_id,
          day: formData.day,
          time_from: formData.time_from,
          time_to: formData.time_to,
          description: formData.description
        });
        toast.success('Inspection updated successfully.');
        setShowEditModal(false);
        setEditingInspection(null);
      }
      loadData();
    } catch (error) {
      console.error('Error saving inspection:', error);
      toast.error(error.response?.data?.message || 'Failed to save inspection');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle export CSV
  const handleExportCSV = () => {
    const headers = ['Building Manager', 'Day', 'From', 'To', 'Type', 'Status'];
    const csvData = inspections.map(inspection => [
      inspection.buildingManagerName,
      new Date(inspection.day).toLocaleDateString('en-GB'),
      inspection.time_from,
      inspection.time_to,
      inspection.typeName,
      inspection.status
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `property-inspections-${id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('CSV exported successfully!');
  };

  // Handle export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Property Inspections Report', 20, 20);
    
    // Add property info
    doc.setFontSize(12);
    doc.text(`Property: ${property?.name || 'Unknown'}`, 20, 35);
    doc.text(`Address: ${property?.address || 'Unknown'}`, 20, 45);
    
    // Add table headers
    const headers = ['Building Manager', 'Day', 'From', 'To', 'Type'];
    let y = 70;
    
    doc.setFontSize(10);
    headers.forEach((header, index) => {
      doc.text(header, 20 + (index * 35), y);
    });
    
    // Add table data
    y += 10;
    inspections.forEach((inspection, index) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      
      const row = [
        inspection.buildingManagerName || '-',
        inspection.day || '-',
        inspection.time_from || '-',
        inspection.time_to || '-',
        inspection.typeName || '-'
      ];
      
      row.forEach((cell, cellIndex) => {
        doc.text(String(cell), 20 + (cellIndex * 35), y);
      });
      
      y += 10;
    });
    
    doc.save(`property-inspections-${id}.pdf`);
    toast.success('PDF exported successfully!');
  };

  // Handle column visibility
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    buildingManager: true,
    day: true,
    from: true,
    to: true,
    type: true,
    calendar: true,
    actions: true
  });

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Click outside handler for column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnVisibility && !event.target.closest(`.${styles.columnVisibilityContainer}`)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  // Handle search
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filter inspections based on search term
  const filteredInspections = React.useMemo(() => {
    if (!searchTerm) return inspections;
    
    return inspections.filter(inspection => 
      inspection.buildingManagerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.typeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.day?.includes(searchTerm) ||
      inspection.time_from?.includes(searchTerm) ||
      inspection.time_to?.includes(searchTerm)
    );
  }, [inspections, searchTerm]);

  // Paginate filtered inspections
  const paginatedInspections = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredInspections.slice(startIndex, endIndex);
  }, [filteredInspections, currentPage, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredInspections.length / pageSize);

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Dashboard', path: '/' },
    { name: 'Properties', path: '/properties' },
    { name: property?.name || 'Property', path: `/properties/${id}` },
    { name: 'Inspections', path: `/inspections/property/${id}` }
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading property inspections...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
      </div>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((crumb, index) => (
          <span key={index}>
            {index > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
            {index === breadcrumbs.length - 1 ? (
              <span className={styles.breadcrumbItem}>{crumb.name}</span>
            ) : (
              <a
                href={crumb.path}
                className={styles.breadcrumbItem}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
              >
                {crumb.name}
              </a>
            )}
          </span>
        ))}
      </div>

      {/* Main Content */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>
                <span className={styles.textMuted}>Inspections of </span>
                {property?.name}
              </h3>
            </div>
            <div className={styles.panelBody}>
              {/* Calendar */}
              <div className={styles.calendarContainer}>
                <Calendar
                  localizer={localizer}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 500 }}
                  onSelectEvent={handleEventClick}
                  onEventDrop={handleEventDrop}
                  onEventResize={handleEventResize}
                  draggableAccessor={() => true}
                  resizable
                  className={styles.calendar}
                />
              </div>

              {/* Status Overview */}
              <div className={styles.statusOverview}>
                <div className={styles.panelTitle}>
                  <h3>Status overview</h3>
                </div>
                <div className={styles.panelGroup}>
                  <div className={styles.colMd2}>
                    <div 
                      className={`${styles.activityBox} ${styles.jsInspectionsOverviewBox}`}
                      style={{ backgroundColor: '#5bc0de' }}
                      data-status-id="999"
                      onClick={() => handleStatusFilter(999)}
                    >
                      <p className={styles.activityValue}>{inspectionsAll}</p>
                      <p className={styles.activityTitle} data-to-display="all">All</p>
                    </div>
                  </div>

                  <div className={styles.colMd2}>
                    <div 
                      className={`${styles.activityBox} ${styles.jsInspectionsOverviewBox}`}
                      style={{ backgroundColor: 'green' }}
                      data-status-id="1"
                      onClick={() => handleStatusFilter(1)}
                    >
                      <p className={styles.activityValue}>{inspectionsUpcoming}</p>
                      <p className={styles.activityTitle} data-to-display="Upcoming">Upcoming</p>
                    </div>
                  </div>

                  <div className={styles.colMd2}>
                    <div 
                      className={`${styles.activityBox} ${styles.jsInspectionsOverviewBox}`}
                      style={{ backgroundColor: 'red' }}
                      data-status-id="2"
                      onClick={() => handleStatusFilter(2)}
                    >
                      <p className={styles.activityValue}>{inspectionsPast}</p>
                      <p className={styles.activityTitle} data-to-display="Past">Past</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* DataTable */}
              <div className={styles.panel}>
                <div className={styles.panelHeading}>
                  <h3>List</h3>
                </div>
                <div className={styles.panelBody}>
                  {inspections.length > 0 ? (
                    <div className={styles.dataTableWrapper}>
                      <div className={styles.tableToolbar}>
                        <div className={styles.tableControls}>
                          <div className={styles.lengthControl}>
                            <label>Show </label>
                            <select 
                              className={styles.pageSizeSelect}
                              value={pageSize}
                              onChange={handlePageSizeChange}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                            <label> entries</label>
                          </div>
                          <div className={styles.searchControl}>
                            <label>Search: </label>
                            <input
                              type="text"
                              className={styles.searchInput}
                              value={searchTerm}
                              onChange={handleSearch}
                              placeholder="Search inspections..."
                            />
                          </div>
                        </div>
                        <div className={styles.tableButtons}>
                          <button 
                            className={`${styles.btn} ${styles.btnDefault}`}
                            onClick={handleExportCSV}
                          >
                            Export as CSV
                          </button>
                          <button 
                            className={`${styles.btn} ${styles.btnDefault}`}
                            onClick={handleExportPDF}
                          >
                            Export as PDF
                          </button>
                          <div className={styles.columnVisibilityContainer}>
                            <button 
                              className={`${styles.btn} ${styles.btnDefault}`}
                              onClick={handleColumnVisibility}
                            >
                              Column Visibility
                            </button>
                            {showColumnVisibility && (
                              <div className={styles.columnVisibilityDropdown}>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.buildingManager}
                                      onChange={() => toggleColumn('buildingManager')}
                                    />
                                    <span className={styles.columnVisibilityText}>Building Manager</span>
                                  </label>
                                </div>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.day}
                                      onChange={() => toggleColumn('day')}
                                    />
                                    <span className={styles.columnVisibilityText}>Day</span>
                                  </label>
                                </div>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.from}
                                      onChange={() => toggleColumn('from')}
                                    />
                                    <span className={styles.columnVisibilityText}>From</span>
                                  </label>
                                </div>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.to}
                                      onChange={() => toggleColumn('to')}
                                    />
                                    <span className={styles.columnVisibilityText}>To</span>
                                  </label>
                                </div>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.type}
                                      onChange={() => toggleColumn('type')}
                                    />
                                    <span className={styles.columnVisibilityText}>Type</span>
                                  </label>
                                </div>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.calendar}
                                      onChange={() => toggleColumn('calendar')}
                                    />
                                    <span className={styles.columnVisibilityText}>Calendar</span>
                                  </label>
                                </div>
                                <div className={styles.columnVisibilityItem}>
                                  <label className={styles.columnVisibilityLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.columnVisibilityCheckbox}
                                      checked={visibleColumns.actions}
                                      onChange={() => toggleColumn('actions')}
                                    />
                                    <span className={styles.columnVisibilityText}>Actions</span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <table className={`${styles.table} ${styles.tableHover} ${styles.dtResponsive}`}>
                        <thead>
                          <tr>
                            {visibleColumns.buildingManager && <th>Building Manager</th>}
                            {visibleColumns.day && <th>Day</th>}
                            {visibleColumns.from && <th className={styles.jsTooltip} data-toggle="tooltip" data-placement="top" title="Time From">From</th>}
                            {visibleColumns.to && <th className={styles.jsTooltip} data-toggle="tooltip" data-placement="top" title="Time To">To</th>}
                            {visibleColumns.type && <th>Type</th>}
                            {visibleColumns.calendar && <th>Calendar</th>}
                            {visibleColumns.actions && <th className={styles.actionHead}>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedInspections.map((inspection) => (
                            <tr key={inspection.id}>
                              {visibleColumns.buildingManager && <td>{inspection.buildingManagerName}</td>}
                              {visibleColumns.day && <td>{inspection.day || '-'}</td>}
                              {visibleColumns.from && <td>{inspection.time_from}</td>}
                              {visibleColumns.to && <td>{inspection.time_to}</td>}
                              {visibleColumns.type && <td>{inspection.typeName}</td>}
                              {visibleColumns.calendar && (
                                <td>
                                  {inspection.building_manager_id && (
                                    <a 
                                      href={`/inspections/building-manager/${inspection.building_manager_id}`}
                                      className={`${styles.btn} ${styles.btnSm} ${styles.btnWarning}`}
                                    >
                                      Building Manager
                                    </a>
                                  )}
                                </td>
                              )}
                              {visibleColumns.actions && (
                                <td className={styles.actionHead}>
                                  <a 
                                    href={`/inspections/${inspection.id}/edit`}
                                    className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
                                  >
                                    Edit
                                  </a>
                                  <a 
                                    href={`/properties/${inspection.property_id}/monthly-inspections`}
                                    className={`${styles.btn} ${styles.btnSm} ${styles.btnInfo}`}
                                  >
                                    Monthly Inspections
                                  </a>
                                  <button 
                                    className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
                                    onClick={() => handleDeleteInspection(inspection.id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Pagination */}
                      <div className={styles.paginationWrapper}>
                        <div className={styles.paginationInfo}>
                          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredInspections.length)} of {filteredInspections.length} entries
                        </div>
                        <div className={styles.pagination}>
                          <button
                            className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = i + 1;
                            return (
                              <button
                                key={page}
                                className={`${styles.btn} ${styles.btnSm} ${currentPage === page ? styles.btnPrimary : styles.btnDefault}`}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            );
                          })}
                          <button
                            className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.noInspections}>No inspections!</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Inspection Modal */}
      {showAddModal && (
        <div className={`${styles.modal} ${styles.fade}`} role="dialog">
          <div className={styles.modalDialog}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <button 
                  type="button" 
                  className={`${styles.close} ${styles.btnModalClose}`} 
                  onClick={() => setShowAddModal(false)}
                >
                  &times;
                </button>
                <h4 className={styles.modalTitle}>Add inspection</h4>
              </div>
              <div className={styles.modalBody}>
                <form className={styles.modalForm} onSubmit={handleSubmit}>
                  <fieldset className={styles.formHorizontal}>
                    {/* Property Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3}`}>Property</label>
                      <div className={styles.colSm9}>
                        <input 
                          className={`${styles.formControl} ${styles.jsPropertyFieldTypeahead}`}
                          type="text" 
                          placeholder="Type property ID or name (or *)"
                          value={propertySearchValue}
                          readOnly
                        />
                        <input 
                          className={styles.jsPropertyIdField}
                          name="property_id" 
                          type="hidden" 
                          value={formData.property_id}
                        />
                      </div>
                    </div>

                    {/* Building Manager Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>BM</label>
                      <div className={styles.colSm5}>
                        <select 
                          className={`${styles.formControl}`} 
                          name="building_manager_id"
                          required
                          value={formData.building_manager_id}
                          onChange={handleInputChange}
                        >
                          <option value="">Please select</option>
                          {buildingManagers.map(manager => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Type Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Type</label>
                      <div className={styles.colSm5}>
                        <select 
                          className={`${styles.formControl} ${styles.fieldType}`} 
                          name="type_id"
                          required
                          value={formData.type_id}
                          onChange={handleInputChange}
                        >
                          <option value="">Please select</option>
                          {inspectionTypes.map(type => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Day Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Day</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          type="text"
                          name="day"
                          placeholder="dd/mm/yyyy"
                          required
                          value={formData.day}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Time From Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Time From</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={`${styles.formControl} ${styles.jsTimepicker}`}
                          type="text"
                          name="time_from"
                          placeholder="HH:mm"
                          required
                          value={formData.time_from}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Time To Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Time To</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={`${styles.formControl} ${styles.jsTimepicker}`}
                          type="text"
                          name="time_to"
                          placeholder="HH:mm"
                          required
                          value={formData.time_to}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Description Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3}`}>Description</label>
                      <div className={styles.colSm9}>
                        <textarea 
                          className={styles.formControl} 
                          rows="3"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>
                    </div>
                  </fieldset>

                  <div className={`${styles.formGroup} ${styles.textRight}`}>
                    <button 
                      type="submit" 
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles.modalSubmitButton}`}
                      onClick={handleSubmit}
                      disabled={formLoading}
                    >
                      {formLoading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
              <div className={styles.modalFooter}>
                <button 
                  type="button" 
                  className={`${styles.btn} ${styles.btnDefault} ${styles.btnModalClose}`}
                  onClick={() => setShowAddModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Inspection Modal */}
      {showEditModal && editingInspection && (
        <div className={`${styles.modal} ${styles.fade}`} role="dialog">
          <div className={styles.modalDialog}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <button 
                  type="button" 
                  className={`${styles.close} ${styles.btnModalClose}`} 
                  onClick={() => setShowEditModal(false)}
                >
                  &times;
                </button>
                <h4 className={styles.modalTitle}>Edit inspection</h4>
              </div>
              <div className={styles.modalBody}>
                <form className={styles.modalForm} onSubmit={handleSubmit}>
                  <input type="hidden" name="id" value={editingInspection.id} />
                  
                  <fieldset className={styles.formHorizontal}>
                    {/* Property Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3}`}>Property</label>
                      <div className={styles.colSm9}>
                        <input 
                          className={`${styles.formControl} ${styles.jsPropertyFieldTypeahead}`}
                          type="text" 
                          placeholder="Type property ID or name (or *)"
                          value={propertySearchValue}
                          readOnly
                        />
                        <input 
                          className={styles.jsPropertyIdField}
                          name="property_id" 
                          type="hidden" 
                          value={formData.property_id}
                        />
                      </div>
                    </div>

                    {/* Building Manager Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>BM</label>
                      <div className={styles.colSm5}>
                        <select 
                          className={`${styles.formControl}`} 
                          name="building_manager_id"
                          required 
                          value={formData.building_manager_id}
                          onChange={handleInputChange}
                        >
                          <option value="">Please select</option>
                          {buildingManagers.map(manager => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Type Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Type</label>
                      <div className={styles.colSm5}>
                        <select 
                          className={`${styles.formControl} ${styles.fieldType}`} 
                          name="type_id"
                          required 
                          value={formData.type_id}
                          onChange={handleInputChange}
                        >
                          <option value="">Please select</option>
                          {inspectionTypes.map(type => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Day Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Day</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          type="text"
                          name="day"
                          placeholder="dd/mm/yyyy"
                          required
                          value={formData.day}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Time From Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Time From</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={`${styles.formControl} ${styles.jsTimepicker}`}
                          type="text"
                          name="time_from"
                          placeholder="HH:mm"
                          required
                          value={formData.time_from}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Time To Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}>Time To</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={`${styles.formControl} ${styles.jsTimepicker}`}
                          type="text"
                          name="time_to"
                          placeholder="HH:mm"
                          required
                          value={formData.time_to}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Description Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3}`}>Description</label>
                      <div className={styles.colSm9}>
                        <textarea 
                          className={styles.formControl} 
                          rows="3" 
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                        ></textarea>
                      </div>
                    </div>

                    {/* Updated At Field */}
                    <div className={styles.formGroup}>
                      <label className={`${styles.controlLabel} ${styles.colSm3}`}>Updated At</label>
                      <div className={styles.colSm5}>
                        <input 
                          className={styles.formControl}
                          type="text"
                          name="updated_at"
                          value={formData.updated_at || 'N/A'}
                          disabled
                        />
                      </div>
                    </div>
                  </fieldset>

                  <div className={`${styles.formGroup} ${styles.pullLeft} ${styles.inlineBlock}`}>
                    <button 
                      type="button" 
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles.jsModalDeleteButton} ${styles.bckRed}`}
                      onClick={() => handleDeleteInspection(editingInspection.id)}
                      disabled={formLoading}
                    >
                      Delete
                    </button>
                  </div>

                  <div className={`${styles.formGroup} ${styles.pullRight} ${styles.inlineBlock}`}>
                    <button 
                      type="submit" 
                      className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles.modalSubmitButton}`}
                      disabled={formLoading}
                    >
                      {formLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>

                  <div className={styles.clearfix}></div>
                </form>
              </div>
              <div className={styles.modalFooter}>
                <button 
                  type="button" 
                  className={`${styles.btn} ${styles.btnDefault} ${styles.btnModalClose}`}
                  onClick={() => setShowEditModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyInspections; 